import { useMsal } from "@azure/msal-react";
import {
    Checkbox,
    DefaultButton, Dropdown,
    IDropdownOption,
    IPanelProps,
    IRenderFunction,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Separator,
    Stack, Text, TextField,
    Toggle
} from "@fluentui/react";
import { RootState } from "app/store";
import DatePickerControl from "components/controls/DatePickerControl";
import InformationCallout from "components/controls/InformationCallout";
import { IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { emitCustomEvent, fadeIn, panelButtonsStyles } from "global/Application";
import { ISaveInstruction } from "global/Application/dataActions";
import { useAuthentication } from "global/Authentication";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { ESnapshotStatus } from "global/Reducer/statusSlice";
import { formatDate } from "global/Utility/TSFunctions";
import { ValidateGCProperties } from "global/Validation/DataValidation";
import {
    account as IXrmOptions_account,
    bcbs_groupconfigurationsnapshot as IXrmOptions_bcbs_groupconfigurationsnapshot,
} from "model/AttributeOptionSets.type";
import { IPanelEvent } from "model/Extension";
import {
    EGroupConfigurationStatus as FAM_EGroupConfigurationStatus,
    IOptionSetValue as FAM_IOptionSetValue,
    TGroupConfigurationStatusOptionSetValue as FAM_TGroupConfigurationStatusOptionSetValue
} from "model/FlexibleAttributeModel";
import { blankSnapshot } from "model/snapshotTemplate";
import { useEffect, useMemo, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { IGroupConfigurationUpdate } from "../../../model/IGroupConfigurationUpdate";
import ConfirmationDialog from "../ConfirmationDialog";
import OpportunityLookup from "../Controls/OpportunityLookup";
import ValidationDialog from "../ValidationDialog";
import { EValidationState, IValidationModel } from "../ValidationDialog/ValidationDialog.type";
import { IGroupConfigurationDataProperties } from "./IGroupConfigurationDataProperties";
import Revision from "./Revision/Revision";


interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss?: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IGroupConfigurationUpdate>) => void;

    variant: "setup" | "change";

    defaultValue?: IGroupConfigurationDataProperties;

}

const GroupConfigurationProperties = (props: IProps) => {

    const { authorizations } = useAuthentication();
    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const [confirmationDialogStatus, setConfirmationDialog] = useState({
        hidden: true
    });

    // current view state/stages
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    // selected category
    const [value, setValue] = useState<IGroupConfigurationDataProperties | null>(null);

    const [viewModel, setViewModel] = useState({
        fundingTypeChanged: false,
        fundingTypeUpdateMatching: true,
        fundingTypeUpdateNonMatching: false,
    });

    useEffect(() => {
        if (value?.dualFunded?.value === false)
            setViewModel(t => {
                return {
                    ...t,
                    fundingTypeUpdateNonMatching: false,
                }
            });
    }, [value]);

    const creatingFrom = props.state.status.snapshotStatus !== ESnapshotStatus.untracked && props.variant === "setup";
    const creatingNew = props.state.status.snapshotStatus === ESnapshotStatus.untracked && props.variant === "setup";

    // #601452 - When status is Cancellation in Progress, auto-set Change Type to Comprehensive Change
    // #730582 - Exception: Don't apply this logic if current change type is Post Cancel Update
    // #731392 - Removed 'Cancelled' from the check; only 'Cancellation in Progress' applies
    const applyComprehensiveChangeIfCancelling = (properties: IGroupConfigurationDataProperties): IGroupConfigurationDataProperties => {
        const comprehensiveChangeValue = IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"].value;
        const postCancelUpdateValue = IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Post Cancel Update(808900015)"].value;
        const isCancelling =
            properties.status?.value === FAM_EGroupConfigurationStatus["Cancellation in Progress"];

        // #730582 - Don't update to Comprehensive Change if change type is Post Cancel Update
        const isPostCancelUpdate = properties.changeType?.value === postCancelUpdateValue;

        if (isCancelling && properties.changeType?.value !== comprehensiveChangeValue && !isPostCancelUpdate) {
            const comprehensiveChangeType = props.state.data.config.cache.options_bcbs_changetypecode
                .find(o => o?.data?.value === comprehensiveChangeValue)?.data ?? null;
            if (comprehensiveChangeType)
                return { ...properties, changeType: comprehensiveChangeType };
        }

        return properties;
    };

    // #731392 - When status is Cancelled, Post Cancel Update is the only valid Change Type.
    // #735944 - Status must also be auto-corrected to Cancellation in Progress; otherwise the
    // cloned record keeps the (disabled, non-selectable) Cancelled status, which is not a valid
    // status to save a new in-progress Post Cancel Update record under.
    const applyPostCancelUpdateIfCancelled = (properties: IGroupConfigurationDataProperties): IGroupConfigurationDataProperties => {
        const postCancelUpdateValue = IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Post Cancel Update(808900015)"].value;

        if (properties.status?.value === FAM_EGroupConfigurationStatus["Cancelled"] &&
            properties.changeType?.value !== postCancelUpdateValue) {
            const postCancelUpdateType = props.state.data.config.cache.options_bcbs_changetypecode
                .find(o => o?.data?.value === postCancelUpdateValue)?.data ?? null;

            const cancellationInProgressStatus = props.state.data.config.cache.options_groupConfigurationStatusReasons
                .find(o => o?.data?.value === FAM_EGroupConfigurationStatus["Cancellation in Progress"])?.data ?? null;

            if (postCancelUpdateType)
                return {
                    ...properties,
                    changeType: postCancelUpdateType,
                    status: cancellationInProgressStatus ?? properties.status,
                };
        }

        return properties;
    };

    //https://dev.azure.com/blue-infosys/Group%20Library/_sprints/taskboard/Group%20Library%20Team/Group%20Library/Sprint%208?workitem=199239
    const changeTypeOptions = useMemo(() => {

        /*
            NA users should not be able to initiate 
            - comprehensive change
            - financial change
            - financial renewal
            - comprehensive renewal
            - stop loss change types.
        */
        const isLobNationalAlliance = props.state.data.json.lineOfBusiness?.value === IXrmOptions_account.bcbs_lineofbusiness["National Alliance(808900001)"].value

        const hasRoleSystemAdmin = authorizations?.roles["BCBS - Power User"];

        // power users should have access to all change types
        const hasRolePowerUser = authorizations?.roles["BCBS - Power User"];

        // service or sales should have access to all except PSA
        const hasRoleSales = authorizations?.roles["BCBS - Sales"];
        const hasRoleService = authorizations?.roles["BCBS - Service"];

        // PSA should have access to PSA
        const hasRolePSA = !!authorizations?.roles['BCBS - PSA'];

        // const canEditPSA = (groupConfigurationProperties?.changeType?.value === CRMOptionSet.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['PSA Change(808900012)'].value
        //     && hasRolePSA
        // );

        //MNIL *** Continuation -> no change allowed



        // Initial Setup -> no change allowed
        // const isInitial = groupConfigurationProperties?.changeType?.value === bcbs_groupconfigurationchangetypecode.InitialSetup.value;

        // Comprehensive Renewal -> no change allowed
        // const isComprehensiveRenewal = groupConfigurationProperties?.changeType?.value === bcbs_groupconfigurationchangetypecode.ComprehensiveRenewal.value;

        // Comprehensive Change -> no change allowed
        // const isComprehensiveChange = groupConfigurationProperties?.changeType?.value === bcbs_groupconfigurationchangetypecode.ComprehensiveChange.value;

        // Benefit Renewal -> Comprehensive only
        // const isBenefitRenewal = groupConfigurationProperties?.changeType?.value === bcbs_groupconfigurationchangetypecode.BenefitRenewal.value;

        // Benefit Change -> Comprehensive only
        // const isBenefitChange = groupConfigurationProperties?.changeType?.value === bcbs_groupconfigurationchangetypecode.BenefitChange.value;

        // PSA Change -> Comprehensive only
        // const isPSAChange = groupConfigurationProperties?.changeType?.value === bcbs_groupconfigurationchangetypecode.PSAChange.value;

        // Financial Change -> Comprehensive only
        // const isFinancialChange = groupConfigurationProperties?.changeType?.value === bcbs_groupconfigurationchangetypecode.FinancialChange.value;

        // Financial Change -> Comprehensive only
        // const isFinancialRenewal = groupConfigurationProperties?.changeType?.value === bcbs_groupconfigurationchangetypecode.FinancialRenewal.value;

        // Administrative Change -> can go to Benefit, Financial, or Comprehensive only
        const isAdministrativeChange = value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Administrative Change(808900003)"].value;

        //MNIL *** Continuation -> no change allowed
        // const isMNIL = groupConfigurationProperties?.changeType?.value === bcbs_groupconfigurationchangetypecode.MNILBenefitChangeContinuation.value ||
        //     groupConfigurationProperties?.changeType?.value === bcbs_groupconfigurationchangetypecode.MNILInitialSetupContinuation.value ||
        //     groupConfigurationProperties?.changeType?.value === bcbs_groupconfigurationchangetypecode.MNILRenewalContinuation.value;


        // Stop Loss -> Financial, Comprehensive only
        const isStopLossChange = value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Stop Loss(808900009)"].value;

        const changeTypeOptions: IDropdownOption<FAM_IOptionSetValue<number>>[] = [];

        const isCancelled = value?.status?.value === FAM_EGroupConfigurationStatus["Cancelled"];
        const isCancellationInProgress = value?.status?.value === FAM_EGroupConfigurationStatus["Cancellation in Progress"];
        const postCancelUpdateValue = IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Post Cancel Update(808900015)"].value;
        const isPostCancelUpdate = value?.changeType?.value === postCancelUpdateValue;
        const isCreatingFromCancellationScenario = creatingFrom && (isCancelled || isCancellationInProgress || isPostCancelUpdate);
        const currentChangeTypeValue = value?.changeType?.value;
        const comprehensiveChangeValue = IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"].value;
        const comprehensiveRenewalValue = IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Renewal(808900001)"].value;

        /*
        Change Type Scenarios:
        1) brand new snapshot
            - only Initial Setup
            * MNIL is locked in this mode, I think AB did this due to a technical reason
        2) editing snapshot and deciding to update the changetype via properties
            - there are rules governing this mode
            - always from less permissive to more permissive
            - Rules:
                - admin change -> anything
                - final change      -> comprehensive change
                - final change      -> comprehensive renewal
                - benefit change    -> comprehensive change
                - benefit change    -> comprehensive renewal
                - benefit change    -> benefit renewal

                * change and renewals may swap
                * in case of falling back to change, opportunity needs to be wipped out (MUal mentioned this)
        3) creating a new snapshot from an existing snapshot due to terminal state of the previous snapshot
            - they can create with any change type other than 'initial setup'
        
            *** if this gets reimplemented, I think instead of nested iff its better to implement using grid logic map
        */
        props.state.data.config.cache.options_bcbs_changetypecode.forEach(t => {

            const option: IDropdownOption<FAM_IOptionSetValue<number>> = { ...t! };
            const optionValue = option.data?.value;

            let hide = true;

            switch (optionValue) {
                case IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"].value:
                    if (!(hasRoleSystemAdmin || hasRolePowerUser || hasRoleSales || hasRoleService))
                        break;

                    // if (!(isBenefitChange || isFinancialChange || isBenefitRenewal || isFinancialRenewal))
                    //     break;

                    // if (!(isAdministrativeChange || isStopLossChange || creatingFrom))
                    //     break;

                    if (creatingNew)
                        break;

                    // Removed isCancelled break - Comprehensive Change must remain visible
                    // when status is Cancelled (auto-set by #601452)
                    // if (isCancelled)
                    //     break;

                    // #731392 - For cancellation flow scenarios while creating from existing,
                    // Comprehensive Change should only be visible via Admin Override.
                    if (isCreatingFromCancellationScenario)
                        break;

                    // if (isLobNationalAlliance)
                    //     break;

                    hide = false;
                    break;

                case IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Change(808900000)"].value:

                    if (!(hasRoleSystemAdmin || hasRolePowerUser || hasRoleSales || hasRoleService))
                        break;

                    // 321879 - Update Change Type Update Options
                    // Allow switching between the following change types:
                    // Benefit Change <--> Benefit Renewal
                    const isBenefitRenewal = value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Renewal(808900010)"].value;

                    if (!(isAdministrativeChange || creatingFrom || isBenefitRenewal))
                        break;


                    if (isCancelled)
                        break;

                    // 459815 - When starting with an Administrative Change Type, User is restricted to upgrading the Change Type to a Comprehensive Change only.
                    if (!creatingFrom && isAdministrativeChange)
                        break;

                    hide = false;
                    break;

                case IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Financial Change(808900006)"].value:
                    if (!(hasRoleSystemAdmin || hasRolePowerUser || hasRoleSales || hasRoleService))
                        break;

                    // 321879 - Update Change Type Update Options
                    // Allow switching between the following change types:
                    // Financial Change <--> Financial Renewal
                    const isFinancialRenewal = value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Financial Renewal(808900011)"].value;

                    if (!(isAdministrativeChange || isStopLossChange || creatingFrom || isFinancialRenewal))
                        break;

                    if (isLobNationalAlliance)
                        break;

                    if (isCancelled)
                        break;

                    // 459815 - When starting with an Administrative Change Type, User is restricted to upgrading the Change Type to a Comprehensive Change only.
                    if (!creatingFrom && isAdministrativeChange)
                        break;

                    hide = false;
                    break;

                case IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Renewal(808900001)"].value:
                    if (!(hasRoleSystemAdmin || hasRolePowerUser || hasRoleSales || hasRoleService))
                        break;

                    // if (!creatingFrom)
                    //     break;

                    if (creatingNew)
                        break;

                    // #731392 - For cancellation flow scenarios while creating from existing,
                    // Comprehensive Renewal should only be visible via Admin Override.
                    if (isCreatingFromCancellationScenario)
                        break;

                    // if (isLobNationalAlliance)
                    //     break;

                    if (isCancelled)
                        break;


                    // 459815 - When starting with an Administrative Change Type, User is restricted to upgrading the Change Type to a Comprehensive Change only.
                    if (!creatingFrom && isAdministrativeChange)
                        break;

                    hide = false;
                    break;

                case IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Renewal(808900010)"].value:
                    if (!(hasRoleSystemAdmin || hasRolePowerUser || hasRoleSales || hasRoleService))
                        break;

                    // 321879 - Update Change Type Update Options
                    // Allow switching between the following change types:
                    // Benefit Change <--> Benefit Renewal
                    const isBenefitChange = value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Change(808900000)"].value;

                    if (!(creatingFrom || isBenefitChange))
                        break;

                    if (isCancelled)
                        break;

                    // 459815 - When starting with an Administrative Change Type, User is restricted to upgrading the Change Type to a Comprehensive Change only.
                    if (!creatingFrom && isAdministrativeChange)
                        break;

                    hide = false;
                    break;


                case IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Financial Renewal(808900011)"].value:
                    if (!(hasRoleSystemAdmin || hasRolePowerUser || hasRoleSales || hasRoleService))
                        break;

                    // 321879 - Update Change Type Update Options
                    // Allow switching between the following change types:
                    // Financial Change <--> Financial Renewal
                    const isFinancialChange = value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Financial Change(808900006)"].value;

                    if (!(creatingFrom || isFinancialChange))
                        break;

                    if (isLobNationalAlliance)
                        break;

                    if (isCancelled)
                        break;

                    // 459815 - When starting with an Administrative Change Type, User is restricted to upgrading the Change Type to a Comprehensive Change only.
                    if (!creatingFrom && isAdministrativeChange)
                        break;

                    hide = false;
                    break;

                case IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Administrative Change(808900003)"].value:
                    if (!(hasRoleSystemAdmin || hasRolePowerUser || hasRoleSales || hasRoleService))
                        break;

                    if (!creatingFrom)
                        break;

                    if (isCancelled)
                        break;

                    hide = false;
                    break;

                case IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Stop Loss(808900009)"].value:
                    if (!(hasRoleSystemAdmin || hasRolePowerUser || hasRoleSales || hasRoleService))
                        break;

                    if (!creatingFrom)
                        break;

                    if (isLobNationalAlliance)
                        break;

                    if (isCancelled)
                        break;

                    // 459815 - When starting with an Administrative Change Type, User is restricted to upgrading the Change Type to a Comprehensive Change only.
                    if (!creatingFrom && isAdministrativeChange)
                        break;

                    hide = false;
                    break;

                case IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["PSA Change(808900012)"].value:
                    if (!(hasRoleSystemAdmin || hasRolePowerUser || hasRolePSA))
                        break;

                    if (!creatingFrom)
                        break;

                    if (isCancelled)
                        break;

                    // 459815 - When starting with an Administrative Change Type, User is restricted to upgrading the Change Type to a Comprehensive Change only.
                    if (!creatingFrom && isAdministrativeChange)
                        break;

                    hide = false;
                    break;

                case IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Post Cancel Update(808900015)"].value:

                    if (!creatingFrom)
                        break;

                    if (!isCancelled)
                        break;

                    // 459815 - When starting with an Administrative Change Type, User is restricted to upgrading the Change Type to a Comprehensive Change only.
                    if (!creatingFrom && isAdministrativeChange)
                        break;

                    hide = false;
                    break;

                default:
                    break;
            }

            // for current selected option, if we are not creating from, allow choosing
            if (!creatingFrom)
                if (props.state.data.json.changeType?.value === optionValue) {
                    hide = false;
                }

            // #731392 - In cancellation create-from scenarios, keep current Comprehensive
            // selection visible (read-only) while still only allowing Post Cancel Update
            // as the actionable change type in non-admin mode.
            if (!props.state.status.debug.admin &&
                isCreatingFromCancellationScenario &&
                currentChangeTypeValue === optionValue &&
                (optionValue === comprehensiveChangeValue || optionValue === comprehensiveRenewalValue)) {
                hide = false;
                option.disabled = true;
            }

            // #731392 - If Status = Cancelled, the only Change Type is Post Cancel Update;
            // every other Change Type is hidden.
            // #735944 - Once Change Type is (or becomes) Post Cancel Update, it must remain the
            // only selectable Change Type regardless of Status (e.g. after auto-correcting Status
            // to Cancellation in Progress), to prevent switching away to another Change Type.
            if (isCancelled || isPostCancelUpdate) {
                hide = optionValue !== postCancelUpdateValue;
                if (!hide)
                    option.disabled = false;
            }

            option.hidden = hide;

            if (props.state.status.debug.diagnosis) {
                if (hide) {
                    option.disabled = true;
                    option.hidden = false;
                }
            }

            if (props.state.status.debug.admin) {
                if (hide) {
                    option.disabled = false;
                    option.hidden = false;
                    option.text = option.text + ' [Admin Override]'
                }
            }

            changeTypeOptions.push(option);
        });

        return changeTypeOptions ?? [];

    }, [value]);

    const statusOptions = useMemo(() => {
        const statusOptions: IDropdownOption<FAM_TGroupConfigurationStatusOptionSetValue>[] = [];

        props.state.data.config.cache.options_groupConfigurationStatusReasons.forEach(eachStatus => {

            const t: IDropdownOption<FAM_TGroupConfigurationStatusOptionSetValue> = {
                ...eachStatus,
            }

            let disabled = false;
            switch (eachStatus.data?.value) {
                case FAM_EGroupConfigurationStatus["Active"]:
                    if (value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Post Cancel Update(808900015)"].value)
                        disabled = true;
                    break;

                case FAM_EGroupConfigurationStatus["Cancelled"]:
                    disabled = true;
                    break;

                case FAM_EGroupConfigurationStatus["Transferred to Other LOB in Progress"]:
                    if (value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Post Cancel Update(808900015)"].value)
                        disabled = true;
                    break;

                case FAM_EGroupConfigurationStatus["Transferred to Other LOB"]:
                    disabled = true;
                    break;

            }

            t.disabled = disabled;

            statusOptions.push(t);
        });

        return statusOptions;
    }, [value?.changeType?.value]);

    //TODO: remove dependency
    useEffect(() => {
        if (props.isOpen) {


            // any props that we need to build state on component mount?
            // reset on start
            if (props.defaultValue) {
                // cloning only properties that I need for clone, (properties that this component changes);
                const gcDefaultProperties = props.defaultValue;

                const gcProperties: IGroupConfigurationDataProperties = {
                    MNIL: gcDefaultProperties.MNIL,
                    changeType: gcDefaultProperties.changeType,
                    dualFunded: gcDefaultProperties.dualFunded,
                    effectiveDate: gcDefaultProperties.effectiveDate,
                    erisa: gcDefaultProperties.erisa,
                    fundingType: gcDefaultProperties.fundingType,
                    grandFathered: gcDefaultProperties.grandFathered,
                    lineOfBusiness: gcDefaultProperties.lineOfBusiness,
                    showInActives: gcDefaultProperties.showInActives,
                    reconciliation: gcDefaultProperties.reconciliation,
                    bcbs_shortname: gcDefaultProperties.bcbs_shortname,
                    status: gcDefaultProperties.status,
                    opportunity: gcDefaultProperties.opportunity,
                    operativeDate: gcDefaultProperties.operativeDate,
                    isRevision: gcDefaultProperties.isRevision,
                    revisionTasks: gcDefaultProperties.revisionTasks,
                };

                if (props.variant === 'setup' && props.state.status.creating) {
                    gcProperties.changeType = null;
                    gcProperties.opportunity = null;
                    gcProperties.operativeDate = {
                        type: 'date',
                        value: formatDate(new Date(), 'local:yyyy-MM-dd')
                    }

                    // Reconsideration flag needs to clear for clone
                    // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/331725
                    gcProperties.reconciliation = { type: 'bool', value: false };

                    gcProperties.isRevision = { type: 'bool', value: false };
                    gcProperties.revisionTasks = [];
                }

                // #731392 - Change Type is no longer auto-corrected to Comprehensive Change on panel open;
                // that promotion happens only on Continue. However, when Status = Cancelled the only
                // valid Change Type is Post Cancel Update, so default it here.
                setValue(structuredClone(applyPostCancelUpdateIfCancelled(gcProperties)));
                setViewModel({
                    fundingTypeChanged: false,
                    fundingTypeUpdateMatching: true,
                    fundingTypeUpdateNonMatching: false,
                });
            }
            else {
                const defaults = blankSnapshot();

                const patch: IGroupConfigurationDataProperties = {

                    MNIL: defaults.MNIL,
                    changeType: defaults.changeType,
                    dualFunded: defaults.dualFunded,
                    effectiveDate: defaults.effectiveDate,
                    erisa: defaults.erisa,
                    fundingType: defaults.fundingType,
                    grandFathered: defaults.grandFathered,
                    lineOfBusiness: defaults.lineOfBusiness,
                    reconciliation: defaults.reconciliation,
                    bcbs_shortname: defaults.bcbs_shortname,
                    showInActives: defaults.showInActives,
                    status: defaults.status,
                    operativeDate: defaults.operativeDate,
                    isRevision: defaults.isRevision,
                    revisionTasks: defaults.revisionTasks,
                }

                setValue(patch);
            }
            setValidationModel(null);
        }

    }, [props.isOpen, props.defaultValue]);

    const isMNIL =
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Benefit Change Continuation(808900007)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Initial Setup Continuation(808900005)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Renewal Continuation(808900008)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Comprehensive Renewal Continuation(808900014)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Comprehensive Change Continuation(808900013)"].value;

    // Check if Opportunity is allowed for the current change type
    // Per PBI 688090: Users should be able to relate Opportunity only for these 7 change types
    const isOpportunityAllowed = 
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Initial Setup(808900002)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Initial Setup Continuation(808900005)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Renewal(808900010)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Renewal(808900001)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Comprehensive Renewal Continuation(808900014)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Renewal Continuation(808900008)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Financial Renewal(808900011)"].value;

    const isOpportunityRequired = value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Initial Setup(808900002)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Renewal(808900010)"].value ||
        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Renewal(808900001)"].value;

    const isMNILChange = props.defaultValue?.MNIL && props.state.status.creating;
    if (isMNILChange && value?.MNIL?.value) {
        if (props.defaultValue?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Initial Setup(808900002)"].value) {
            setValue({
                ...value!,
                changeType: {
                    name: IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Initial Setup Continuation(808900005)"].name,
                    value: IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Initial Setup Continuation(808900005)"].value,
                    type: "optionset"
                }, MNIL: { type: "bool", value: false }
            })
        } else if (props.defaultValue?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Change(808900000)"].value) {
            setValue({
                ...value!,
                changeType: {
                    name: IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Benefit Change Continuation(808900007)"].name,
                    value: IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Benefit Change Continuation(808900007)"].value,
                    type: "optionset"
                }, MNIL: { type: "bool", value: false }
            })
        } else if (props.defaultValue?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Renewal(808900010)"].value) {
            setValue({
                ...value!,
                changeType: {
                    name: IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Renewal Continuation(808900008)"].name,
                    value: IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Renewal Continuation(808900008)"].value,
                    type: "optionset"
                }, MNIL: { type: "bool", value: false }
            })
        } else if (props.defaultValue?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"].value) {
            setValue({
                ...value!,
                changeType: {
                    name: IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Comprehensive Change Continuation(808900013)"].name,
                    value: IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Comprehensive Change Continuation(808900013)"].value,
                    type: "optionset"
                }, MNIL: { type: "bool", value: false }
            })
        } else if (props.defaultValue?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Renewal(808900001)"].value) {
            setValue({
                ...value!,
                changeType: {
                    name: IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Comprehensive Renewal Continuation(808900014)"].name,
                    value: IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Comprehensive Renewal Continuation(808900014)"].value,
                    type: "optionset"
                }, MNIL: { type: "bool", value: false }
            })
        }
        else {
            setValue({ ...value!, MNIL: { type: "bool", value: false } })
        }
    }

    const disabled = false;

    function onRenderProperties(): JSX.Element {

        if (!value)
            return <div>GroupConfiguration not available!</div>;

        return (
            <div className={fadeIn}>
                <Stack tokens={{ childrenGap: '15px' }}>

                    <Stack.Item>
                        <TextField
                            label={"Group Short Name"}
                            value={value.bcbs_shortname ?? ''}
                            disabled={disabled}
                            onChange={(_, newValue) => {
                                setValue(value => { return { ...value!, bcbs_shortname: newValue ?? '' } });
                            }}
                        />
                    </Stack.Item>

                    <Stack.Item>
                        <Dropdown
                            label={"Line of business"}
                            options={props.state.data.config.cache.options_bcbs_lob ?? []}
                            selectedKey={value.lineOfBusiness?.value ?? undefined}
                            disabled={true}
                            onChange={(_, option?: IDropdownOption<FAM_IOptionSetValue>) => {
                                setValue((t) => { return { ...t!, lineOfBusiness: option?.data ?? null } });
                            }}
                        />
                    </Stack.Item>

                    <Stack.Item>
                        <Stack horizontal tokens={{ childrenGap: '15px' }} horizontalAlign="space-between">
                            <Toggle
                                label='MNIL'
                                checked={value.MNIL?.value ?? false}
                                onChange={(_, checked) => {
                                    setValue((t) => { return { ...t!, MNIL: { type: 'bool', value: checked ?? false } } });
                                }}
                                disabled={disabled || isMNIL || props.state.status.creating}
                            />
                            <Toggle
                                label="Dual-Funded"
                                checked={value.dualFunded?.value ?? false}
                                onChange={(_, checked) => {
                                    setValue((t) => { return { ...t!, dualFunded: { type: 'bool', value: checked ?? false } } });
                                }}
                                disabled={disabled || isMNIL}
                            />
                            <Toggle
                                label="Reconciliation"
                                checked={value.reconciliation?.value ?? false}
                                onChange={(_, checked) => {
                                    setValue((t) => { return { ...t!, reconciliation: { type: 'bool', value: checked ?? false } } });
                                }}
                                disabled={disabled || isMNIL}
                            />
                            <Toggle
                                label="Revision"
                                checked={value.isRevision?.value ?? false}
                                onChange={(_, checked) => {
                                    setValue((t) => {
                                        return {
                                            ...t!,
                                            isRevision: { type: 'bool', value: checked ?? false },
                                            revisionTasks: !checked ? [] : (t?.revisionTasks ?? [])
                                        }
                                    });
                                }}
                                disabled={disabled || isMNIL}
                            />
                        </Stack>

                    </Stack.Item>

                    {value.isRevision?.value && value.effectiveDate?.value &&
                        <Stack.Item>
                            <Revision
                                value={value.revisionTasks}
                                properties={value}
                                creatingFrom={creatingFrom}
                                onDelete={(entity) => {
                                    setValue((t) => {
                                        const revisionTasks = t?.revisionTasks?.filter(q => q.id !== entity.id) ?? [];
                                        return { ...t!, revisionTasks: revisionTasks }
                                    });
                                }}
                                onAddOrUpdate={(entity) => {
                                    setValue((t) => {
                                        const revisionTasks = t?.revisionTasks?.filter(q => q.id !== entity.id) ?? [];
                                        revisionTasks.push(entity)
                                        return { ...t!, revisionTasks: revisionTasks }
                                    });
                                }}
                                disabled={disabled}
                            />
                        </Stack.Item>
                    }

                    <Stack.Item>
                        <Dropdown
                            label={"Change Type"}
                            options={changeTypeOptions ?? []}
                            selectedKey={value.changeType?.value ?? undefined}
                            onChange={(_, option?: IDropdownOption<FAM_IOptionSetValue>) => {

                                setValue((xValue) => {
                                    const nValue: typeof xValue = {
                                        ...xValue!,
                                        changeType: option?.data ?? null
                                    }

                                    if (nValue.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Post Cancel Update(808900015)"].value) {
                                        const tStatus = statusOptions.find(q => q.data?.value === FAM_EGroupConfigurationStatus["Cancellation in Progress"])!.data;
                                        if (tStatus)
                                            nValue.status = tStatus;
                                    }

                                    // Clear Opportunity if the new change type doesn't allow it
                                    const newChangeTypeAllowsOpportunity = 
                                        nValue.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Initial Setup(808900002)"].value ||
                                        nValue.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Initial Setup Continuation(808900005)"].value ||
                                        nValue.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Renewal(808900010)"].value ||
                                        nValue.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Renewal(808900001)"].value ||
                                        nValue.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Comprehensive Renewal Continuation(808900014)"].value ||
                                        nValue.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Renewal Continuation(808900008)"].value ||
                                        nValue.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Financial Renewal(808900011)"].value;

                                    if (!newChangeTypeAllowsOpportunity && xValue?.opportunity) {
                                        nValue.opportunity = null;
                                    }

                                    return nValue;
                                });
                            }}
                            disabled={disabled || isMNIL}
                        />
                    </Stack.Item>


                    <Stack.Item>
                        <OpportunityLookup
                            searchOnMount={false}
                            account={props.state.data.account!}
                            value={value.opportunity ?? null}
                            required={isOpportunityRequired}
                            onPick={(opportunity) => {
                                setValue((t) => { return { ...t!, opportunity: opportunity } });
                            }}
                            disabled={disabled || !isOpportunityAllowed}

                        />
                    </Stack.Item>

                    <Stack.Item>
                        <DatePickerControl
                            label={"Effective Date"}
                            defaultFieldValue={value.effectiveDate}
                            fieldValueOnChange={(event, value) => {
                                setValue((t) => { return { ...t!, effectiveDate: value } });
                            }}
                            disabled={disabled || isMNIL}
                        />
                    </Stack.Item>

                    <Stack.Item>
                        <Stack tokens={{ childrenGap: '5px' }} >
                            <Stack.Item>
                                <Dropdown
                                    label={"Funding Type"}
                                    options={props.state.data.config.cache.options_bcbs_fundingtypes ?? []}
                                    selectedKey={value.fundingType?.value ?? undefined}
                                    onChange={(_, option?: IDropdownOption<FAM_IOptionSetValue>) => {
                                        setValue(t => { return { ...t!, fundingType: option?.data ?? null } });
                                        setViewModel(t => { return { ...t, fundingTypeChanged: true } });
                                    }}
                                    disabled={disabled || isMNIL}
                                />
                            </Stack.Item>
                            {(
                                (props.variant === 'change' && viewModel.fundingTypeChanged) ||
                                (props.variant === "setup" && props.state.status.snapshotStatus !== ESnapshotStatus.untracked && viewModel.fundingTypeChanged)
                            ) && <>
                                    <Stack.Item>
                                        <Stack horizontal verticalAlign="center" >
                                            <Checkbox
                                                label={'Update Funding Type on matching entities.'}
                                                styles={{ root: { display: 'inline' } }}
                                                checked={viewModel.fundingTypeUpdateMatching}
                                                disabled={true}
                                                onChange={(event, checked) => {
                                                    setViewModel(t => { return { ...t, fundingTypeUpdateMatching: checked ?? false } });
                                                }}
                                            />
                                            <InformationCallout hover>
                                                <Text variant="small">
                                                    <p>Each Plan and Benefit with same Funding Type prior to change, will be updated to new Funding Type.</p>
                                                    <p>Visibility rules will also apply when Funding Type is updated.</p>
                                                </Text>
                                            </InformationCallout>
                                        </Stack>
                                    </Stack.Item>
                                    <Stack.Item>
                                        <Stack horizontal verticalAlign="end">
                                            <Checkbox
                                                label={'Update Funding Type on non-matching entities.'}
                                                styles={{ root: { display: 'inline' } }}
                                                disabled={disabled}
                                                checked={viewModel.fundingTypeUpdateNonMatching}
                                                onChange={(event, checked) => {
                                                    setViewModel(t => { return { ...t, fundingTypeUpdateNonMatching: checked ?? false } });
                                                }}
                                            />
                                            <InformationCallout hover>
                                                <Text variant="small">An example would be a Dual-Funded entity where Funding Type does not match Group Configuration's Funding Type</Text>
                                            </InformationCallout>
                                        </Stack>
                                    </Stack.Item>
                                </>
                            }
                        </Stack>
                    </Stack.Item>


                    <Stack.Item>
                        <Dropdown
                            label={"ERISA"}
                            options={props.state.data.config.cache.options_bcbs_erisacode ?? []}
                            selectedKey={value.erisa?.value}
                            onChange={(_, option?: IDropdownOption<FAM_IOptionSetValue>) => {

                                setValue((t) => { return { ...t!, erisa: option?.data! } });

                                if (option?.data?.name === 'ERISA') {
                                    setConfirmationDialog({
                                        hidden: false
                                    });
                                }
                            }}
                            disabled={disabled || isMNIL}
                        />
                    </Stack.Item>

                    {(
                        props.variant === 'change' ||
                        value?.changeType?.value === IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Post Cancel Update(808900015)"].value
                    ) &&
                        <Stack.Item>
                            <Dropdown
                                label={"Status"}
                                options={statusOptions}
                                selectedKey={value?.status?.value}
                                disabled={disabled}
                                onChange={(_, option?: IDropdownOption<FAM_TGroupConfigurationStatusOptionSetValue>) => {
                                    if (option?.data) {
                                        // #731392 - Do not change Change Type here; Comprehensive Change
                                        // is applied only after clicking Continue.
                                        setValue(t => { return { ...t!, status: option.data! } });
                                    }
                                }}
                            />
                        </Stack.Item>
                    }

                    {props.variant === 'change' &&
                        <Stack.Item>
                            <Toggle
                                label='Show Deactivated Only'
                                checked={value.showInActives?.value ?? false}
                                disabled={disabled}
                                onChange={(_, checked) => {
                                    setValue((t) => { return { ...t!, showInActives: { type: 'bool', value: checked ?? false } } });
                                }}
                            />
                        </Stack.Item>
                    }

                </Stack>
            </div>
        );
    }

    function onConfirm() {
        const { fundingTypeUpdateMatching, fundingTypeUpdateNonMatching } = viewModel;

        if (value) {

            // #731392 - Apply the Comprehensive Change promotion only after clicking Continue.
            const groupConfigurationChanges = applyComprehensiveChangeIfCancelling(value);

            props.onConfirm?.({
                value: {
                    groupConfigurationChanges: groupConfigurationChanges,
                    instructions: {
                        fundingTypeUpdateMatching: fundingTypeUpdateMatching,
                        fundingTypeUpdateNonMatching: fundingTypeUpdateNonMatching,
                        fundingTypeChanged: viewModel.fundingTypeChanged,
                    }
                }
            });
        }
    }

    function onRenderPropertiesFooter(): JSX.Element | null {
        if (!value)
            return <div>Group Configuration not available!</div>;

        let disabledContinue = disabled ||
            !value ||
            !value.bcbs_shortname ||
            value.bcbs_shortname === '' ||
            value.fundingType === null ||
            value.erisa === null ||
            value.grandFathered === null ||
            value.dualFunded === null ||
            value.effectiveDate === null ||
            value.changeType === null ||
            (isOpportunityRequired && !value.opportunity);

        if (props.state.status.debug.admin)
            disabledContinue = false;

        return <>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={disabledContinue}
                onClick={async () => {

                    let canContinue = true;

                    // implementing validation infrastructure
                    // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/321774
                    try {
                        const validationMessages: IValidationMessage[] = [];

                        setValidationModel({
                            validationMessages: validationMessages,
                            state: EValidationState.Validating,
                            data: {},
                        });

                        const validationResults = await ValidateGCProperties(dataSource, props.state, props.variant, value);

                        validationMessages.push(...validationResults);

                        // let the validation form continue
                        if (validationMessages.length > 0) {

                            setValidationModel({
                                validationMessages: validationMessages,
                                state: EValidationState.PendingUser,
                                data: {},
                            });

                            canContinue = false;
                        }

                        if (canContinue)
                            onConfirm();

                    } catch (error) {

                        emitCustomEvent({
                            eventType: 'globalException',
                            details: error
                        });

                        throw error

                    } finally {

                    }
                }}

            />
            {props.variant !== "setup" &&
                <DefaultButton text='Cancel'
                    onClick={props.onDismiss}
                    styles={panelButtonsStyles}
                />
            }
            {props.variant === "setup" &&
                <DefaultButton text='Cancel'
                    onClick={props.onCancel ?? props.onDismiss}
                    styles={panelButtonsStyles}
                />
            }
        </>
    }

    const [validationModel, setValidationModel] = useState<IValidationModel<ISaveInstruction> | null>(null);

    const onRenderValidationMessage: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {

        if (validationMessage)
            return <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                {validationMessage}
            </MessageBar>;

        return <></>;

    }

    const onRenderBody: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderProperties;

        return <div className={fadeIn}>
            <Separator />
            {onRenderValidationMessage()}
            {onRender()}
        </div>;
    }

    const onRenderFooter: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderPropertiesFooter;
        return onRender();
    }

    const onRenderErisaConfirmation = () => {
        return <ConfirmationDialog
            hidden={confirmationDialogStatus.hidden}
            onRenderMessage={() => {
                return (
                    <div>
                        By making this change, you are confirming that you understand the following:<br />
                        <span style={{ color: 'red' }}>ERISA does NOT apply to the following types of group plans:</span>
                        <ul>
                            <li>Government entity</li>
                            <li>Church</li>
                            <li>Plans outside the US</li>
                            <li>Plans solely for workers compensation, disability or unemployment</li>
                            <li>Unfunded excess benefit plans</li>
                        </ul>
                        <span>Are you sure you want to continue?</span>
                    </div>
                )
            }}
            actions={[
                {
                    name: "Continue",
                    primary: true,
                    onClick: () => {

                        setConfirmationDialog({ hidden: true });
                    }
                },
                {
                    name: "Cancel",
                    onClick: () => {
                        setConfirmationDialog({ hidden: true });
                        const opt = props.state.data.config.cache.options_bcbs_erisacode.find(q => q.data?.name === 'Non-ERISA' || q.data?.value === 808900000)!;
                        if (opt?.data) {
                            const _data: IGroupConfigurationDataProperties = { ...value!, erisa: opt.data };
                            setValue(_data);
                        }

                    }
                },
            ]}
        />;
    }

    const onRenderValidationDialog = () => {
        if (props.isOpen && validationModel)
            return <ValidationDialog
                defaultValue={validationModel}
                loadingPageDuringValidation={true}
                onCancel={() => {
                    setValidationModel(null);
                }}
                onContinue={(e: IValidationModel) => {
                    onConfirm();
                }}
            />
        return <></>;
    }

    const onRenderPanel = () => {
        return <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            type={PanelType.medium}
            isLightDismiss={false}
            hasCloseButton={false}
            closeButtonAriaLabel="Close"
            headerText="Group Configuration Properties"
            onRenderFooterContent={onRenderFooter}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
            {onRenderValidationDialog()}
            {onRenderErisaConfirmation()}
        </Panel>;
    }

    return onRenderPanel();
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(GroupConfigurationProperties);
